import React, {useEffect, useState} from 'react';
import {Text} from '@gravity-ui/uikit';
import {ActionModal, ErrorModal, FormInput, useErrorHandler} from '../../../../shared';
import {CategoryGroupModel} from '../../../../entities';
import {useEditCategoryGroupForm} from '../../model';
import {useCategoryGroupImageUpload} from '../../lib';
import {CategoryGroupImageUploadButton} from '../CategoryGroupImageUploadButton';
import styles from './EditCategoryGroupModal.module.scss';

interface EditCategoryGroupModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categoryGroup: CategoryGroupModel | null;
}

export const EditCategoryGroupModal = ({open, setOpen, categoryGroup}: EditCategoryGroupModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    isLoading,
    submitError,
    resetForm,
    submitForm,
  } = useEditCategoryGroupForm({setOpen, categoryGroup});

  const {uploadId, isUploading, uploadImage, resetUpload} = useCategoryGroupImageUpload();
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Группа категорий',
    title: 'Ошибка при редактировании группы категорий',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
      setRemoveExistingImage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoryGroup?.id]);

  useEffect(() => {
    setFieldValue('imageUploadId', uploadId || null);
    if (uploadId) {
      setFieldValue('imageFileName', null);
    }
  }, [uploadId, setFieldValue]);

  useEffect(() => {
    if (removeExistingImage) {
      setFieldValue('imageFileName', null);
    }
  }, [removeExistingImage, setFieldValue]);

  const handleFileSelect = (file: File) => {
    setRemoveExistingImage(false);
    uploadImage(file);
  };

  const handleRemoveImage = () => {
    if (uploadId) {
      resetUpload();
      setFieldValue('imageUploadId', null);
    }
    if (categoryGroup?.imageUrl && !removeExistingImage) {
      setRemoveExistingImage(true);
    }
  };

  const displayedImageUrl =
    !uploadId && !removeExistingImage ? categoryGroup?.imageUrl ?? null : null;

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Редактировать группу'}
        textButtonApply={'Сохранить'}
        textButtonCancel={'Отменить'}
        isDisabled={isUploading}>
        <FormInput
          label="Название группы"
          placeholder="Название группы"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.name && errors.name}
        />
        <FormInput
          label="Порядок показа"
          placeholder="Порядок показа"
          name="order"
          type="number"
          value={values.order.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.order && errors.order}
        />
        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Изображение
          </Text>
          <CategoryGroupImageUploadButton
            imageUrl={displayedImageUrl}
            isUploading={isUploading}
            onFileSelect={handleFileSelect}
            onRemove={handleRemoveImage}
          />
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
